import { useEffect, useRef } from "react";

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "summary",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const firstSummary = (details: HTMLDetailsElement) =>
  Array.from(details.children).find(
    (child): child is HTMLElement => child instanceof HTMLElement && child.tagName === "SUMMARY",
  );

const isHiddenByClosedDetails = (element: HTMLElement, dialog: HTMLElement) => {
  let ancestor = element.parentElement;
  while (ancestor && ancestor !== dialog) {
    if (ancestor instanceof HTMLDetailsElement && !ancestor.open) {
      const summary = firstSummary(ancestor);
      if (!summary?.contains(element)) return true;
    }
    ancestor = ancestor.parentElement;
  }
  return false;
};

const isRendered = (element: HTMLElement, dialog: HTMLElement) => {
  let current: HTMLElement | null = element;
  while (current && current !== dialog) {
    if (
      current.hidden ||
      current.inert ||
      current.hasAttribute("inert") ||
      current.getAttribute("aria-hidden") === "true"
    ) {
      return false;
    }
    const style = window.getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse" ||
      style.contentVisibility === "hidden"
    ) {
      return false;
    }
    current = current.parentElement;
  }
  return true;
};

const isTabbable = (element: HTMLElement, dialog: HTMLElement) =>
  element.tabIndex >= 0 &&
  !element.matches(":disabled") &&
  isRendered(element, dialog) &&
  !isHiddenByClosedDetails(element, dialog);

export function useModalDialog<T extends HTMLElement>(onClose: () => void) {
  const dialogRef = useRef<T>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!dialog) return;
    const backdrop = dialog.parentElement;
    const siblings = backdrop?.parentElement
      ? Array.from(backdrop.parentElement.children).filter(
          (element): element is HTMLElement =>
            element instanceof HTMLElement && element !== backdrop,
        )
      : [];
    const previousInert = siblings.map((element) => element.inert);
    siblings.forEach((element) => {
      element.inert = true;
    });

    const focusableElements = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (element) => isTabbable(element, dialog),
      );

    const requestedInitialFocus = dialog.querySelector<HTMLElement>(
      "[data-dialog-initial-focus]",
    );
    const initialFocus =
      (requestedInitialFocus && isTabbable(requestedInitialFocus, dialog)
        ? requestedInitialFocus
        : undefined) ??
      focusableElements()[0] ??
      dialog;
    let lastDialogFocus: HTMLElement | null = initialFocus;
    initialFocus.focus();

    const focusFirst = () => (focusableElements()[0] ?? dialog).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof Node)) return;
      if (!dialog.contains(event.target)) {
        focusFirst();
      } else if (event.target instanceof HTMLElement) {
        lastDialogFocus = event.target;
      }
    };

    const observer = new MutationObserver(() => {
      const activeElement = document.activeElement;
      if (
        activeElement === dialog ||
        (activeElement instanceof HTMLElement &&
          dialog.contains(activeElement) &&
          isTabbable(activeElement, dialog))
      ) {
        return;
      }

      const closedDetails = lastDialogFocus?.closest("details:not([open])");
      const summary =
        closedDetails instanceof HTMLDetailsElement
          ? firstSummary(closedDetails)
          : undefined;
      if (summary && isTabbable(summary, dialog)) {
        summary.focus();
      } else {
        focusFirst();
      }
    });

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    observer.observe(dialog, {
      attributes: true,
      attributeFilter: [
        "aria-hidden",
        "class",
        "disabled",
        "hidden",
        "inert",
        "open",
        "style",
        "tabindex",
      ],
      childList: true,
      subtree: true,
    });
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      observer.disconnect();
      siblings.forEach((element, index) => {
        element.inert = previousInert[index] ?? false;
      });
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  return dialogRef;
}
